<template>
	<div class="container__crud">
		<div class="container__crudactions">
			<div class="container__crudactions__cruddownload">
				<div class="container__crudactions__cruddownload__crud">
					<Button
						:label="$t('create')"
						color="trv-default"
						semantic-class="tiny"
						width-auto
						@click="showNewApiKeyForm"
					/>
					<Button
						:label="$t('documentation')"
						color="trv-primary"
						semantic-class="tiny"
						width-auto
						style="margin-left: 0.5rem"
						@click="showDocumentation"
					>
						<i class="fas fa-book" style="margin-right: 0.5rem"></i>
						{{ $t('documentation') }}
					</Button>
				</div>
			</div>
			<div class="container__crudactions__filtercollums">
				<div class="container__crudactions__filtercollums__filter">
					<InputSearch
						v-model="textFilter"
						style="margin-right: 0.5rem"
						:placeholder="$t('search')"
					/>
				</div>
			</div>
		</div>

		<div class="api-keys-list">
			<div
				v-for="(apiKey, index) in filteredApiKeys"
				:key="index"
				class="api-key-card"
			>
				<div class="api-key-header">
					<div class="api-key-info">
						<h3 class="api-key-name">{{ apiKey.name }}</h3>
						<span
							class="api-key-level"
							:class="`level-${apiKey.accessLevel}`"
						>
							{{ getAccessLevelLabel(apiKey.accessLevel) }}
						</span>
					</div>
					<button
						class="btn-revoke"
						:title="$t('revokeApiKey')"
						@click="confirmRevoke(apiKey)"
					>
						<i class="fas fa-trash-alt"></i>
					</button>
				</div>
				<div class="api-key-details">
					<div class="detail-item">
						<span class="detail-label">{{ $t('apiKey') }}:</span>
						<span class="detail-value masked-key">{{ apiKey.maskedKey }}</span>
					</div>
					<div class="detail-item">
						<span class="detail-label">{{ $t('createdAt') }}:</span>
						<span class="detail-value">{{ formatDate(apiKey.createdAt) }}</span>
					</div>
					<div
						v-if="apiKey.lastUsedAt"
						class="detail-item"
					>
						<span class="detail-label">{{ $t('lastUsedAt') }}:</span>
						<span class="detail-value">{{ formatDate(apiKey.lastUsedAt) }}</span>
					</div>
				</div>
			</div>

			<div
				v-if="filteredApiKeys.length === 0"
				class="empty-state"
			>
				<i class="fas fa-key"></i>
				<p>{{ $t('noApiKeys') }}</p>
			</div>
		</div>

		<!-- Modal para criar API Key -->
		<CrudForm
			:is-visible="showForm"
			:change-is-visible="closeForm"
			:title-form="$t('createApiKey')"
			:backdrop="true"
		>
			<template #form>
				<div style="display: flex; flex-direction: column; gap: 1rem">
					<InputText
						v-model="newApiKey.name"
						:label="$t('apiKeyName')"
						:placeholder="$t('apiKeyNamePlaceholder')"
					/>

					<div>
						<label class="label">{{ $t('accessLevel') }}</label>
						<div class="access-level-options">
							<div
								v-for="level in accessLevels"
								:key="level.value"
								class="access-level-option"
								:class="{ selected: newApiKey.accessLevel === level.value }"
								@click="newApiKey.accessLevel = level.value"
							>
								<div class="level-header">
									<i :class="level.icon"></i>
									<span class="level-name">{{ $t(level.label) }}</span>
								</div>
								<p class="level-description">{{ $t(level.description) }}</p>
							</div>
						</div>
					</div>

					<div class="container__crud__crudform__buttons">
						<Button
							:label="$t('cancel')"
							color="trv-secondary"
							semantic-class="mini"
							outlined
							@click="closeForm"
						/>
						<Button
							:label="$t('generate')"
							color="trv-default"
							semantic-class="mini"
							:disabled="!newApiKey.name || !newApiKey.accessLevel"
							@click="createNewApiKey"
						/>
					</div>
				</div>
			</template>
		</CrudForm>

		<!-- Modal para exibir API Key gerada -->
		<CrudForm
			:is-visible="showGeneratedKeyModal"
			:change-is-visible="closeGeneratedKeyModal"
			:title-form="$t('apiKeyGenerated')"
			:backdrop="true"
		>
			<template #form>
				<div style="display: flex; flex-direction: column; gap: 1rem">
					<div class="success-message">
						<i class="fas fa-check-circle"></i>
						<p>{{ $t('apiKeyGeneratedSuccess') }}</p>
					</div>

					<div class="generated-key-container">
						<label class="label">{{ $t('yourApiKey') }}:</label>
						<div class="key-display">
							<code class="generated-key">{{ generatedKey }}</code>
							<button
								class="btn-copy"
								:title="$t('copy')"
								@click="copyToClipboard"
							>
								<i :class="copied ? 'fas fa-check' : 'fas fa-copy'"></i>
							</button>
						</div>
					</div>

					<div class="warning-message">
						<i class="fas fa-exclamation-triangle"></i>
						<p>{{ $t('apiKeyWarning') }}</p>
					</div>

					<div class="container__crud__crudform__buttons">
						<Button
							:label="$t('close')"
							color="trv-default"
							semantic-class="mini"
							@click="closeGeneratedKeyModal"
						/>
					</div>
				</div>
			</template>
		</CrudForm>

		<!-- Modal de Documentação -->
		<CrudForm
			:is-visible="showDocumentationModal"
			:change-is-visible="closeDocumentation"
			:title-form="''"
			:backdrop="true"
			class="documentation-modal"
		>
			<template #form>
				<div class="documentation-modal-content">
					<ApiKeyDocumentation />
				</div>
			</template>
		</CrudForm>
	</div>
</template>

<script>
import { mapMutations } from 'vuex'
import Button from '../../../components/Button/Button'
import CrudForm from '../../../components/CrudForm/CrudForm'
import InputSearch from '../../../components/InputSearch/InputSearch'
import InputText from '../../../components/InputText/InputText'
import ApiKeyDocumentation from './ApiKeyDocumentation.vue'
import { listApiKeys, createApiKey, revokeApiKey } from '../../../services/requests/ApiKeyRequests'

export default {
	name: 'ApiKey',
	components: {
		Button,
		CrudForm,
		InputSearch,
		InputText,
		ApiKeyDocumentation,
	},
	data() {
		return {
			apiKeys: [],
			textFilter: '',
			showForm: false,
			showGeneratedKeyModal: false,
			showDocumentationModal: false,
			generatedKey: '',
			copied: false,
			newApiKey: {
				name: '',
				accessLevel: null,
			},
			accessLevels: [
				{
					value: 1,
					label: 'readOnly',
					description: 'readOnlyDescription',
					icon: 'fas fa-eye',
				},
			//	{
			//		value: 2,
				//	label: 'standard',
				//	description: 'standardDescription',
				//	icon: 'fas fa-edit',
			//	},
			//	{
			//		value: 3,
			//		label: 'admin',
			//		description: 'adminDescription',
			//		icon: 'fas fa-user-shield',
			//	},
			],
		}
	},
	computed: {
		filteredApiKeys() {
			if (!this.textFilter) {
				return this.apiKeys
			}
			const filter = this.textFilter.toLowerCase()
			return this.apiKeys.filter((key) => key.name.toLowerCase().includes(filter))
		},
	},
	created() {
		this.loadApiKeys()
	},
	methods: {
		...mapMutations('moduleMessages', ['mutationShowMessage']),

		async loadApiKeys() {
			try {
				const response = await listApiKeys()
				console.log('Response from listApiKeys:', response.data)
				this.apiKeys = response.data.apiKeys.map((apiKey) => {
					console.log('Processing API Key:', apiKey.key)
					const masked = this.maskApiKey(apiKey.key)
					console.log('Masked key:', masked)
					return {
						name: apiKey.name,
						accessLevel: apiKey.accessLevel,
						maskedKey: masked,
						fullKey: apiKey.key,
						createdAt: apiKey.createdAt,
						lastUsedAt: apiKey.lastUsedAt,
						customerId: apiKey.customerId,
					}
				})
				console.log('Final apiKeys array:', this.apiKeys)
			} catch (error) {
				console.error('Erro ao carregar API Keys:', error)
				this.mutationShowMessage({
					messageTitle: this.$t('erro'),
					messageText: error.response?.data?.message || this.$t('apiKeyCreateError'),
					messageIcon: 'far fa-times-circle',
					messageType: 'error',
				})
			}
		},

		showNewApiKeyForm() {
			this.newApiKey = {
				name: '',
				accessLevel: 1, // ReadOnly como padrão
			}
			this.showForm = true
		},

		closeForm() {
			this.showForm = false
		},

		showDocumentation() {
			this.showDocumentationModal = true
		},

		closeDocumentation() {
			this.showDocumentationModal = false
		},

		async createNewApiKey() {
			try {
				const response = await createApiKey({
					name: this.newApiKey.name,
					accessLevel: this.newApiKey.accessLevel,
				})

				const { apiKey } = response.data

				// Recarrega a lista completa do servidor
				await this.loadApiKeys()

				// Exibe a key gerada
				this.generatedKey = apiKey
				this.showForm = false
				this.showGeneratedKeyModal = true

				this.mutationShowMessage({
					messageTitle: this.$t('sucesso'),
					messageText: this.$t('apiKeyCreatedSuccess'),
					messageIcon: 'far fa-check-circle',
					messageType: 'success',
				})
			} catch (error) {
				console.error('Erro ao criar API Key:', error)
				this.mutationShowMessage({
					messageTitle: this.$t('erro'),
					messageText: error.response?.data?.message || this.$t('apiKeyCreateError'),
					messageIcon: 'far fa-times-circle',
					messageType: 'error',
				})
			}
		},

		closeGeneratedKeyModal() {
			this.showGeneratedKeyModal = false
			this.generatedKey = ''
			this.copied = false
		},

		async confirmRevoke(apiKey) {
			const confirmed = confirm(this.$t('confirmRevokeApiKey', { name: apiKey.name }))
			if (confirmed) {
				await this.revokeApiKeyAction(apiKey)
			}
		},

		async revokeApiKeyAction(apiKey) {
			try {
				await revokeApiKey(apiKey.fullKey)

				// Recarrega a lista completa do servidor
				await this.loadApiKeys()

				this.mutationShowMessage({
					messageTitle: this.$t('sucesso'),
					messageText: this.$t('apiKeyRevokedSuccess'),
					messageIcon: 'far fa-check-circle',
					messageType: 'success',
				})
			} catch (error) {
				console.error('Erro ao revogar API Key:', error)
				this.mutationShowMessage({
					messageTitle: this.$t('erro'),
					messageText: error.response?.data?.message || this.$t('apiKeyRevokeError'),
					messageIcon: 'far fa-times-circle',
					messageType: 'error',
				})
			}
		},

		maskApiKey(key) {
			if (!key || key.length < 12) return key
			return key.substring(0, 8) + '...' + key.substring(key.length - 4)
		},

		copyToClipboard() {
			navigator.clipboard.writeText(this.generatedKey).then(() => {
				this.copied = true
				setTimeout(() => {
					this.copied = false
				}, 2000)
			})
		},

		formatDate(dateString) {
			if (!dateString) return '-'
			return new Date(dateString).toLocaleDateString(navigator.language, {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			})
		},

		getAccessLevelLabel(level) {
			const levels = {
				ReadOnly: this.$t('readOnly'),
				Standard: this.$t('standard'),
				Admin: this.$t('admin'),
			}
			return levels[level] || level
		},
	},
}
</script>

<style scoped src="../../../css/crud.css"></style>
<style scoped>
.api-keys-list {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	padding: 1rem 0;
	width: 100%;
}

.api-key-card {
	width: 100%;
	background: white;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	padding: 1.5rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.api-key-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	border-color: var(--color-green);
}

.api-key-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid #f0f0f0;
}

.api-key-info {
	flex: 1;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 1rem;
}

.api-key-name {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 600;
	color: var(--color-navy-blue);
	white-space: nowrap;
}

.api-key-level {
	display: inline-block;
	padding: 0.25rem 0.75rem;
	border-radius: 12px;
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
}

.level-ReadOnly {
	background-color: #e3f2fd;
	color: #1976d2;
}

.level-Standard {
	background-color: #fff3e0;
	color: #f57c00;
}

.level-Admin {
	background-color: #fce4ec;
	color: #c2185b;
}

.btn-revoke {
	background: transparent;
	border: none;
	color: #e53935;
	font-size: 1.1rem;
	cursor: pointer;
	padding: 0.5rem;
	border-radius: 4px;
	transition: all 0.2s ease;
}

.btn-revoke:hover {
	background-color: #ffebee;
}

.api-key-details {
	display: flex;
	flex-direction: row;
	gap: 2rem;
	flex-wrap: wrap;
	align-items: center;
}

.detail-item {
	display: inline-flex;
	flex-direction: row;
	gap: 0.5rem;
	align-items: center;
	flex-shrink: 0;
}

.detail-label {
	font-size: 0.75rem;
	color: #757575;
	font-weight: 600;
	text-transform: uppercase;
	white-space: nowrap;
	flex-shrink: 0;
}

.detail-value {
	font-size: 0.9rem;
	color: var(--color-navy-blue);
	white-space: nowrap;
}

.masked-key {
	font-family: 'Courier New', monospace;
	background-color: #f5f5f5;
	padding: 0.25rem 0.5rem;
	border-radius: 4px;
	font-weight: 600;
	white-space: nowrap;
	flex-shrink: 0;
}

.empty-state {
	grid-column: 1 / -1;
	text-align: center;
	padding: 3rem;
	color: #9e9e9e;
}

.empty-state i {
	font-size: 3rem;
	margin-bottom: 1rem;
	display: block;
}

.empty-state p {
	font-size: 1.1rem;
	margin: 0;
}

/* Access Level Selection */
.access-level-options {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-top: 0.5rem;
}

.access-level-option {
	border: 2px solid #e0e0e0;
	border-radius: 8px;
	padding: 1rem;
	cursor: pointer;
	transition: all 0.3s ease;
}

.access-level-option:hover {
	border-color: var(--color-green);
	background-color: #f9f9f9;
}

.access-level-option.selected {
	border-color: var(--color-green);
	background-color: #f1f8f4;
}

.level-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 0.5rem;
}

.level-header i {
	font-size: 1.25rem;
	color: var(--color-green);
}

.level-name {
	font-weight: 600;
	font-size: 1rem;
	color: var(--color-navy-blue);
}

.level-description {
	margin: 0;
	font-size: 0.85rem;
	color: #757575;
	line-height: 1.4;
}

/* Generated Key Modal */
.success-message {
	text-align: center;
	padding: 1rem;
	background-color: #f1f8f4;
	border-radius: 8px;
}

.success-message i {
	font-size: 3rem;
	color: var(--color-green);
	margin-bottom: 0.5rem;
}

.success-message p {
	margin: 0;
	font-size: 1.1rem;
	color: var(--color-navy-blue);
	font-weight: 600;
}

.generated-key-container {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.key-display {
	display: flex;
	gap: 0.5rem;
	align-items: center;
}

.generated-key {
	flex: 1;
	padding: 1rem;
	background-color: #f5f5f5;
	border: 2px solid var(--color-green);
	border-radius: 8px;
	font-family: 'Courier New', monospace;
	font-size: 0.9rem;
	word-break: break-all;
	color: var(--color-navy-blue);
	font-weight: 600;
}

.btn-copy {
	background-color: var(--color-green);
	color: white;
	border: none;
	border-radius: 8px;
	padding: 1rem;
	cursor: pointer;
	transition: all 0.3s ease;
	font-size: 1.1rem;
}

.btn-copy:hover {
	background-color: var(--color-green-dark, #45a049);
}

.warning-message {
	padding: 1rem;
	background-color: #fff3e0;
	border-left: 4px solid #ff9800;
	border-radius: 4px;
	display: flex;
	gap: 1rem;
	align-items: flex-start;
}

.warning-message i {
	color: #ff9800;
	font-size: 1.5rem;
	margin-top: 0.25rem;
}

.warning-message p {
	margin: 0;
	font-size: 0.9rem;
	color: #e65100;
	line-height: 1.5;
}

.label {
	display: block;
	font-weight: 600;
	margin-bottom: 0.5rem;
	color: var(--color-navy-blue);
}

@media screen and (max-width: 768px) {
	.api-keys-list {
		grid-template-columns: 1fr;
	}

	.access-level-options {
		gap: 0.75rem;
	}
}

/* Centralizar modal de documentação */
.documentation-modal :deep(.container__crudform__panel) {
	position: fixed !important;
	right: auto !important;
	left: 50% !important;
	top: 50% !important;
	bottom: auto !important;
	transform: translate(-50%, -50%) !important;
	width: 90vw !important;
	max-width: 1200px !important;
	height: auto !important;
	max-height: 90vh !important;
	margin: 0 !important;
	border-radius: 8px !important;
	transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out !important;
}

.documentation-modal :deep(.fa-times) {
	font-size: 1.5rem !important;
	padding: 0.75rem !important;
	cursor: pointer;
	transition: transform 0.2s ease;
}

.documentation-modal :deep(.fa-times:hover) {
	transform: scale(1.1);
	color: #e53935;
}

.documentation-modal :deep(.container__crudform__titleclose) {
	display: none !important;
}

.documentation-modal :deep(.container__crudform__panel:not(.container__crudform__panel__visible)) {
	opacity: 0 !important;
	visibility: hidden !important;
	pointer-events: none !important;
}

.documentation-modal :deep(.container__crudform__panel__visible) {
	right: auto !important;
	left: 50% !important;
	width: 90vw !important;
	max-width: 1200px !important;
	opacity: 1 !important;
	visibility: visible !important;
	pointer-events: auto !important;
}

.documentation-modal-content {
	width: 100%;
	max-height: calc(90vh - 100px);
	overflow-y: auto;
	padding: 0;
}

.documentation-modal-content :deep(.api-documentation) {
	padding: 1rem;
	max-width: 100%;
	margin: 0;
}

@media screen and (max-width: 991px) {
	.documentation-modal :deep(.container__crudform__panel) {
		width: 95vw !important;
		max-width: 95vw !important;
	}

	.documentation-modal :deep(.container__crudform__panel__visible) {
		width: 95vw !important;
		max-width: 95vw !important;
	}
}
</style>
